// @ts-check

/** @typedef {import('./brand-by-target.mjs').Brand} Brand */

/**
 * @typedef {{
 *   brand: Brand;
 *   emails: {
 *     support: string;
 *     security: string | null;
 *     business: string | null;
 *     partners: string | null;
 *   };
 *   features: {
 *     jackpot: boolean;
 *     betting: boolean;
 *   };
 *   currency: {
 *     defaultSymbol: string;
 *     guestCode: string;
 *     guestSymbol: string;
 *   };
 *   assets: {
 *     logo: {
 *       default: string;
 *       mobile: string;
 *       mobileMenu: string;
 *       footer?: string;
 *       dimensions: {
 *         default: { width: number; height: number };
 *         mobile: { width: number; height: number };
 *       };
 *     };
 *     loader: {
 *       default: string;
 *       mobile: string;
 *     };
 *     welcomeBanner: {
 *       default: string;
 *       mobile: string | null;
 *     };
 *     bonusesBanner: (lang: string) => {
 *       default: string;
 *       mobile: string;
 *     };
 *     homeBanner: (lang: string) => {
 *       default: string;
 *       mobile: string;
 *     };
 *     motivationalBanner?: (lang: string) => {
 *       default: string;
 *       mobile: string;
 *     };
 *     storeBanner?: (lang: string) => {
 *       overlay?: string;
 *       mobile: string;
 *       bg: string;
 *     };
 *     cashbackBanner?: (lang: string) => {
 *       overlay?: string;
 *       mobile: string;
 *       bg: string;
 *     };
 *     rebateBanner?: {
 *       default: string;
 *       mobile: string;
 *     };
 *     referralBanner?: (lang: string) => {
 *       default: string;
 *       mobile: string;
 *     };
 *     referralIcon?: string;
 *     statusBanner?: (
 *       lang: string,
 *       level: string,
 *       auth: boolean
 *     ) => {
 *       default: string;
 *       mobile: string;
 *       video?: {
 *         default: string;
 *         mobile: string;
 *       };
 *     };
 *     lastChance?: string;
 *     onboardingPopups?: Partial<Record<import('@/features/onboarding/model/types').OnboardingPopupType, string>>;
 *     application?: {
 *       default: string;
 *     };
 *     installationGuideScreenshot?: {
 *       android: string[];
 *       ios: string[];
 *     };
 *     appLink?: {
 *       android: string;
 *       ios: string;
 *     };
 *     installationGuideBanner?: {
 *       default: string;
 *       mobile: string;
 *     };
 *   };
 * }} BrandTheme
 */

/** @type {Record<Brand, BrandTheme>} */
export const BRAND_THEMES = {
  pw: {
    brand: 'pw',
    emails: {
      support: 'support@pingwin.games',
      security: 'security@pingwin.games',
      business: 'business@pingwin.games',
      partners: 'partners@pingwin.games',
    },
    features: {
      jackpot: true,
      betting: true,
    },
    currency: {
      defaultSymbol: '₽',
      guestCode: 'USD',
      guestSymbol: '$',
    },
    assets: {
      logo: {
        default: '/images/brand/pw/desktop.svg',
        mobile: '/images/brand/pw/mobile.svg',
        mobileMenu: '/images/brand/pw/logo-mobile-2.svg',
        dimensions: {
          default: { width: 120, height: 30 },
          mobile: { width: 46, height: 30 },
        },
      },
      loader: {
        default: '/images/brand/pw/desktop-loader.svg',
        mobile: '/images/brand/pw/mobile-loader.svg',
      },
      welcomeBanner: {
        default: '/images/brand/pw/welcome/welcome.webp',
        mobile: '/images/brand/pw/welcome/welcome-mob.webp',
      },
      bonusesBanner: (lang = 'en') => {
        return {
          default: `/images/brand/pw/bonuses-banner/bonuses-banner-${lang}.webp`,
          mobile: `/images/brand/pw/bonuses-banner/bonuses-banner-${lang}-mobile.webp`,
        };
      },
      homeBanner: (lang = 'en') => {
        return {
          default: `/images/brand/pw/home-banner/home-banner-${lang}.webp`,
          mobile: `/images/brand/pw/home-banner/home-banner-${lang}-mobile.webp`,
        };
      },
      motivationalBanner: (lang = 'ro') => {
        return {
          default: `/images/brand/pw/motivational/motivational-banner-${lang}-desktop.webp`,
          mobile: `/images/brand/pw/motivational/motivational-banner-${lang}-mobile.webp`,
        };
      },
      statusBanner: (lang = 'en', level = '1', auth = false) => {
        const banners = {
          auth: {
            default: `/images/brand/pw/status/status_${level}/desktop/status-banner-${lang}.webp`,
            mobile: `/images/brand/pw/status/status_${level}/mobile/status-banner-${lang}.webp`,
          },
          notAuth: {
            default: `/images/levels/levels-${lang}.webp`,
            mobile: `/images/levels/levels-${lang}-mob.webp`,
          },
        };

        const video = {
          default: `/videos/statuses/status_${level}/desktop/${lang}.mp4`,
          mobile: `/videos/statuses/status_${level}/mobile/${lang}.mp4`,
        };

        return {
          ...banners[auth ? 'auth' : 'notAuth'],
          video,
        };
      },
      storeBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/pw/store/store-${lang}-mobile.webp`,
          bg: `/images/brand/pw/store/store-bg-${lang}.webp`,
        };
      },
      cashbackBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/pw/cashback/cashback-${lang}-mobile.webp`,
          bg: `/images/brand/pw/cashback/cashback-bg-${lang}.webp`,
        };
      },
      lastChance: '/images/brand/pw/last-chance/last-chance.webp',
      application: {
        default: '/images/brand/pw/application/app-menu.webp',
      },
      installationGuideScreenshot: {
        ios: [
          '/images/brand/pw/application/screenshot/ios/1.webp',
          '/images/brand/pw/application/screenshot/ios/2.webp',
          '/images/brand/pw/application/screenshot/ios/3.webp',
          '/images/brand/pw/application/screenshot/ios/4.webp',
          '/images/brand/pw/application/screenshot/ios/5.webp',
          '/images/brand/pw/application/screenshot/ios/6.webp',
        ],
        android: [
          '/images/brand/pw/application/screenshot/android/1.webp',
          '/images/brand/pw/application/screenshot/android/2.webp',
          '/images/brand/pw/application/screenshot/android/3.webp',
          '/images/brand/pw/application/screenshot/android/4.webp',
          '/images/brand/pw/application/screenshot/android/5.webp',
        ],
      },
      installationGuideBanner: {
        default: `/images/brand/pw/application/installation-guide-banner.webp`,
        mobile: `/images/brand/pw/application/installation-guide-banner-mob.webp`,
      },
      appLink: {
        ios: '',
        android: '/assets/apk/PingwinCasinoApp.apk',
      },
    },
  },
  lm: {
    brand: 'lm',
    emails: {
      support: 'support@loto-million.com',
      security: null,
      business: null,
      partners: null,
    },
    features: {
      jackpot: true,
      betting: true,
    },
    currency: {
      defaultSymbol: '₽',
      guestCode: 'USD',
      guestSymbol: '$',
    },
    assets: {
      logo: {
        default: '/images/brand/lm/desktop.svg',
        mobile: '/images/brand/lm/mobile.svg',
        mobileMenu: '/images/brand/lm/logo-mobile-2.svg',
        dimensions: {
          default: { width: 220, height: 43 },
          mobile: { width: 121, height: 38 },
        },
      },
      loader: {
        default: '/images/brand/lm/desktop-loader.svg',
        mobile: '/images/brand/lm/mobile-loader.svg',
      },
      welcomeBanner: {
        default: '/images/brand/lm/welcome/welcome.webp',
        mobile: '/images/brand/lm/welcome/welcome-mob.webp',
      },
      bonusesBanner: (lang = 'ru') => {
        return {
          default: `/images/brand/lm/bonuses-banner/bonuses-banner-${lang}.webp`,
          mobile: `/images/brand/lm/bonuses-banner/bonuses-banner-${lang}-mobile.webp`,
        };
      },
      homeBanner: (lang = 'ru') => {
        return {
          default: `/images/brand/lm/home-banner/home-banner-${lang}.webp`,
          mobile: `/images/brand/lm/home-banner/home-banner-${lang}-mobile.webp`,
        };
      },
      application: {
        default: '/images/brand/lm/application/app-menu.webp',
      },
      statusBanner: (lang = 'en', level = '1', auth = false) => {
        const banners = {
          auth: {
            default: `/images/brand/lm/status/status_${level}/desktop/status-banner-${lang}.webp`,
            mobile: `/images/brand/lm/status/status_${level}/mobile/status-banner-${lang}.webp`,
          },
          notAuth: {
            default: `/images/levels/levels-${lang}.webp`,
            mobile: `/images/levels/levels-${lang}-mob.webp`,
          },
        };

        return {
          ...banners[auth ? 'auth' : 'notAuth'],
        };
      },
      storeBanner: (lang = 'ru') => {
        return {
          overlay: `/images/brand/lm/store/store-${lang}.webp`,
          mobile: `/images/brand/lm/store/store-${lang}-mobile.webp`,
          bg: `/images/brand/lm/store/store-bg.webp`,
        };
      },
      cashbackBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/lm/cashback/cashback-${lang}-mobile.webp`,
          bg: `/images/brand/lm/cashback/cashback-bg.webp`,
        };
      },
      installationGuideScreenshot: {
        ios: [
          '/images/brand/lm/application/screenshot/ios/1.webp',
          '/images/brand/lm/application/screenshot/ios/2.webp',
          '/images/brand/lm/application/screenshot/ios/3.webp',
          '/images/brand/lm/application/screenshot/ios/4.webp',
          '/images/brand/lm/application/screenshot/ios/5.webp',
          '/images/brand/lm/application/screenshot/ios/6.webp',
        ],
        android: [
          '/images/brand/lm/application/screenshot/android/1.webp',
          '/images/brand/lm/application/screenshot/android/2.webp',
          '/images/brand/lm/application/screenshot/android/3.webp',
          '/images/brand/lm/application/screenshot/android/4.webp',
          '/images/brand/lm/application/screenshot/android/5.webp',
        ],
      },
      lastChance: '/images/brand/lm/last-chance/last-chance.webp',
      installationGuideBanner: {
        default: `/images/brand/lm/application/installation-guide-banner.webp`,
        mobile: `/images/brand/lm/application/installation-guide-banner-mob.webp`,
      },
      appLink: {
        ios: '',
        android: '/assets/apk/LotoMillionApp.apk',
      },
    },
  },
  ph: {
    brand: 'ph',
    emails: {
      support: 'support@powerplay.ph',
      security: 'security@powerplay.ph',
      business: 'business@powerplay.ph',
      partners: 'partners@powerplay.ph',
    },
    features: {
      jackpot: false,
      betting: false,
    },
    currency: {
      defaultSymbol: '₱',
      guestCode: 'PHP',
      guestSymbol: '₱',
    },
    assets: {
      logo: {
        default: '/images/brand/ph/desktop.webp',
        mobile: '/images/brand/ph/mobile.webp',
        mobileMenu: '/images/brand/ph/logo-mobile-2.svg',
        footer: '/images/brand/ph/footer.webp',
        dimensions: {
          default: { width: 147, height: 44 },
          mobile: { width: 91, height: 41 },
        },
      },
      loader: {
        default: '/images/brand/ph/desktop-loader.webp',
        mobile: '/images/brand/ph/mobile-loader.webp',
      },
      welcomeBanner: {
        default: '/images/brand/ph/welcome/welcome.webp',
        mobile: '/images/brand/ph/welcome/welcome-mob.webp',
      },
      bonusesBanner: (lang = 'en') => {
        return {
          default: `/images/brand/ph/bonuses-banner/bonuses-banner-en.webp`,
          mobile: `/images/brand/ph/bonuses-banner/bonuses-banner-en-mobile.webp`,
        };
      },
      homeBanner: (lang = 'en') => {
        return {
          default: `/images/brand/ph/home-banner/home-banner-en.webp`,
          mobile: `/images/brand/ph/home-banner/home-banner-en-mobile.webp`,
        };
      },
      application: {
        default: '/images/brand/ph/application/app-menu.webp',
      },
      storeBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/ph/store/store-${lang}-mobile.webp`,
          bg: `/images/brand/ph/store/store-bg-${lang}.webp`,
        };
      },
      cashbackBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/ph/cashback/cashback-${lang}-mobile.webp`,
          bg: `/images/brand/ph/cashback/cashback-bg-${lang}.webp`,
        };
      },
      rebateBanner: {
        default: '/images/brand/ph/rebate/bas.webp',
        mobile: '/images/brand/ph/rebate/bas.webp',
      },
      referralBanner: (lang = 'en') => {
        return {
          default: `/images/brand/ph/referral/banner-pc-${lang}.webp`,
          mobile: `/images/brand/ph/referral/banner-mobile-${lang}.webp`,
        };
      },
      referralIcon: '/images/brand/ph/referral/hero-icon.webp',
      statusBanner: () => {
        return {
          default: `/images/levels/levels-ph.webp`,
          mobile: `/images/levels/levels-ph-mob.webp`,
        };
      },
      installationGuideScreenshot: {
        ios: [
          '/images/brand/ph/application/screenshot/ios/1.webp',
          '/images/brand/ph/application/screenshot/ios/2.webp',
          '/images/brand/ph/application/screenshot/ios/3.webp',
          '/images/brand/ph/application/screenshot/ios/4.webp',
          '/images/brand/ph/application/screenshot/ios/5.webp',
          '/images/brand/ph/application/screenshot/ios/6.webp',
        ],
        android: [
          '/images/brand/ph/application/screenshot/android/1.webp',
          '/images/brand/ph/application/screenshot/android/2.webp',
          '/images/brand/ph/application/screenshot/android/3.webp',
          '/images/brand/ph/application/screenshot/android/4.webp',
          '/images/brand/ph/application/screenshot/android/5.webp',
        ],
      },
      installationGuideBanner: {
        default: `/images/brand/ph/application/installation-guide-banner.webp`,
        mobile: `/images/brand/ph/application/installation-guide-banner-mob.webp`,
      },
      appLink: {
        ios: '',
        android: '/assets/apk/PowerPlayApp.apk',
      },
      lastChance: '/images/brand/ph/last-chance/last-chance.webp',
      onboardingPopups: {
        dailyBonusUnlocked:
          '/images/brand/ph/onboarding/daily-bonus-unlocked.webp',
        depositSuccessful:
          '/images/brand/ph/onboarding/deposit-successful.webp',
        rebate: '/images/brand/ph/onboarding/rebate.webp',
        vipStatus: '/images/brand/ph/onboarding/vip-status.webp',
        firstDepositBonus:
          '/images/brand/ph/onboarding/first-deposit-bonus.webp',
        welcome: '/images/brand/ph/onboarding/welcome.webp',
        loyaltyPoints: '/images/brand/ph/onboarding/loyalty-points.webp',
      },
    },
  },
  ph2: {
    brand: 'ph2',
    emails: {
      support: 'support@pingwin.ph',
      security: 'security@pingwin.ph',
      business: 'business@pingwin.ph',
      partners: 'partners@pingwin.ph',
    },
    features: {
      jackpot: true,
      betting: true,
    },
    currency: {
      defaultSymbol: '₱',
      guestCode: 'PHP',
      guestSymbol: '₱',
    },
    assets: {
      logo: {
        default: '/images/brand/ph2/desktop.svg',
        mobile: '/images/brand/ph2/mobile.svg',
        mobileMenu: '/images/brand/ph2/logo-mobile-2.svg',
        dimensions: {
          default: { width: 120, height: 30 },
          mobile: { width: 46, height: 30 },
        },
      },
      loader: {
        default: '/images/brand/ph2/desktop-loader.svg',
        mobile: '/images/brand/ph2/mobile-loader.svg',
      },
      welcomeBanner: {
        default: '/images/brand/ph2/welcome/welcome.webp',
        mobile: '/images/brand/ph2/welcome/welcome-mob.webp',
      },
      bonusesBanner: (lang = 'en') => {
        return {
          default: `/images/brand/ph2/bonuses-banner/bonuses-banner-en.webp`,
          mobile: `/images/brand/ph2/bonuses-banner/bonuses-banner-en-mobile.webp`,
        };
      },
      homeBanner: (lang = 'en') => {
        return {
          default: `/images/brand/ph2/home-banner/home-banner-en.webp`,
          mobile: `/images/brand/ph2/home-banner/home-banner-en-mobile.webp`,
        };
      },
      application: {
        default: '/images/brand/ph2/application/app-menu.webp',
      },
      storeBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/ph2/store/store-${lang}-mobile.webp`,
          bg: `/images/brand/ph2/store/store-bg-${lang}.webp`,
        };
      },
      cashbackBanner: (lang = 'en') => {
        return {
          mobile: `/images/brand/ph2/cashback/cashback-${lang}-mobile.webp`,
          bg: `/images/brand/ph2/cashback/cashback-bg-${lang}.webp`,
        };
      },
      rebateBanner: {
        default: '/images/brand/ph2/rebate/bas-dt.webp',
        mobile: '/images/brand/ph2/rebate/bas.webp',
      },
      referralBanner: (lang = 'en') => {
        return {
          default: `/images/brand/ph2/referral/banner-pc-${lang}.webp`,
          mobile: `/images/brand/ph2/referral/banner-mobile-${lang}.webp`,
        };
      },
      referralIcon: '/images/brand/ph2/referral/hero-icon.webp',
      statusBanner: () => {
        return {
          default: `/images/levels/levels-ph.webp`,
          mobile: `/images/levels/levels-ph-mob.webp`,
        };
      },
      installationGuideScreenshot: {
        ios: [
          '/images/brand/ph2/application/screenshot/ios/1.webp',
          '/images/brand/ph2/application/screenshot/ios/2.webp',
          '/images/brand/ph2/application/screenshot/ios/3.webp',
          '/images/brand/ph2/application/screenshot/ios/4.webp',
          '/images/brand/ph2/application/screenshot/ios/5.webp',
          '/images/brand/ph2/application/screenshot/ios/6.webp',
        ],
        android: [
          '/images/brand/ph2/application/screenshot/android/1.webp',
          '/images/brand/ph2/application/screenshot/android/2.webp',
          '/images/brand/ph2/application/screenshot/android/3.webp',
          '/images/brand/ph2/application/screenshot/android/4.webp',
          '/images/brand/ph2/application/screenshot/android/5.webp',
        ],
      },
      installationGuideBanner: {
        default: `/images/brand/ph2/application/installation-guide-banner.webp`,
        mobile: `/images/brand/ph2/application/installation-guide-banner-mob.webp`,
      },
      appLink: {
        ios: '',
        android: '/assets/apk/PingWinApp.apk',
      },
      lastChance: '/images/brand/ph2/last-chance/last-chance.webp',
      onboardingPopups: {
        dailyBonusUnlocked:
          '/images/brand/ph2/onboarding/daily-bonus-unlocked.webp',
        depositSuccessful:
          '/images/brand/ph2/onboarding/deposit-successful.webp',
        rebate: '/images/brand/ph2/onboarding/rebate.webp',
        vipStatus: '/images/brand/ph2/onboarding/vip-status.webp',
        firstDepositBonus:
          '/images/brand/ph2/onboarding/first-deposit-bonus.webp',
        welcome: '/images/brand/ph2/onboarding/welcome.webp',
        loyaltyPoints: '/images/brand/ph2/onboarding/loyalty-points.webp',
      },
    },
  },
};
